/* eslint-disable max-lines */
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, OnDestroy, signal, Signal, untracked } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop'; // <-- make sure this import exists
import { FormsModule } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TooltipDirective } from 'ngx-bootstrap/tooltip';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ShieldApiStore, ShieldZoneResponse, WafRuleGroupModel, WafRuleMainGroupModel, WafRuleModel } from '@bn/app-shield';
import { AppRoutes } from '@bn/dashboard-core/enums';
import { AbstractLazyDirective } from '@bn/shared/entities/abstracts/abstract-lazy.directive';
import { StringBooleanMap } from '@bn/shared/entities/models';
import { StoreRequest } from '@bn/shared/state-store';
import { ButtonComponent } from '@bn/web-ui/button/button.component';
import { CheckboxComponent } from '@bn/web-ui/checkbox/checkbox.component';
import { ConfirmationDialogComponent } from '@bn/web-ui/dialog';
import { DropdownMenuItem } from '@bn/web-ui/dropdown-menu';
import { DropdownMenuComponent } from '@bn/web-ui/dropdown-menu/dropdown-menu.component';
import { InputComponent } from '@bn/web-ui/input/input.component';
import { LoadingComponent } from '@bn/web-ui/loading/loading.component';
import { ChildRulesPipe } from '../../../../pipes';
import { WafService } from '../../../../services/waf.service';
import { sortCustomRulesByOrder } from '../../../../stores/helpers/shield-api-store.helper';

@Component({
  templateUrl: './app-shield-waf-rules.page.html',
  styleUrls: ['./app-shield-waf-rules.page.scss'],
  imports: [
    CommonModule,
    FaIconComponent,
    ChildRulesPipe,
    DragDropModule,
    FormsModule,
    ButtonComponent,
    CheckboxComponent,
    DropdownMenuComponent,
    InputComponent,
    LoadingComponent,
    TooltipDirective,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush, // NOTE: Possible bug here. Tested, no problems for now.
})
export class AppShieldWafRulesPage extends AbstractLazyDirective implements OnDestroy {
  #shieldStore = inject(ShieldApiStore);
  #wafService = inject(WafService);
  #dialog = inject(BsModalService);

  private shield = this.#shieldStore.selectedEntity();
  public batchDisableRulesLoading = this.#shieldStore.isLoading(StoreRequest.Update, ['batchDisable']);
  public batchEnableRulesLoading = this.#shieldStore.isLoading(StoreRequest.Update, ['batchEnable']);
  public batchLogRulesLoading = this.#shieldStore.isLoading(StoreRequest.Update, ['batchLog']);

  public noRulesChecked = computed<boolean>(() => !Object.values(this.checkedRules()).some((value) => value));
  public allRulesChecked = computed<boolean>(() => Object.values(this.checkedRules()).every((value) => value));
  public noGroupsChecked = computed<boolean>(() => !Object.values(this.checkedGroups()).some((value) => value));
  public allGroupsChecked = computed<boolean>(() => Object.values(this.checkedGroups()).every((value) => value));

  public checkedRules = signal<StringBooleanMap>({});
  public checkedGroups = signal<StringBooleanMap>({});
  public expandedGroups = signal<StringBooleanMap>({});
  public filteredMainGroups = signal<FEWafRuleMainGroupModel[]>([]);
  public search = signal<string>('');

  // Add a debounced version of the search signal - avoid unnecessary re-renders / tab freezes
  public debouncedSearch = toSignal(toObservable(this.search).pipe(debounceTime(300), distinctUntilChanged()), { initialValue: '' });

  public isReorderSaving = this.#shieldStore.isLoading(StoreRequest.Update, ['wafCustomRuleOrder']);
  public isReorderDisabled = computed<boolean>(() => this.search().trim().length > 0 || this.isReorderSaving());
  // Optimistic order set on drop; null = follow shield().wafCustomRuleOrder.
  private orderOverride = signal<number[] | null>(null);

  public filteredCustomRules = computed<FEWafRuleModel[]>(() =>
    this.debouncedSearch().length > 0 ? this.filterCustomRules(this.customRules(), this.debouncedSearch()) : this.customRules(),
  );

  public customRules = computed<FEWafRuleModel[]>(() => {
    const mapped = this.#shieldStore.wafCustomRules().map((rule) => {
      if (rule.id != null) {
        this.isLoadingRows[`rule-${rule.id}`] = this.#shieldStore.isLoading(StoreRequest.Update, [`rule-${String(rule.id)}`]) as Signal<boolean>;
      }
      return {
        ...rule,
        ruleId: rule.id == null ? undefined : String(rule.id),
        description: rule.ruleName || '',
        isChecked: false,
        isEnabled: !(this.shield()?.wafDisabledRules ?? [])?.includes(String(rule.id)),
        isLogging: (this.shield()?.wafLogOnlyRules ?? [])?.includes(String(rule.id)),
      };
    });

    return sortCustomRulesByOrder(mapped, this.orderOverride() ?? this.shield()?.wafCustomRuleOrder);
  });

  public mainGroups = computed<FEWafRuleMainGroupModel[]>(() => {
    const entity = this.#shieldStore.selectedEntity()();
    if (!entity || entity.planType === undefined) return [];

    const wafRules = this.#shieldStore.wafAvailableRuleGroups()[entity.planType].filter(
      // NOTE: filter out mainGroups if there is no active rules inside
      (mainGroup) => mainGroup.ruleGroups?.filter((ruleGroup) => !!ruleGroup.rules?.length).length,
    );

    return this.mapRulesToLocalModel(wafRules, entity);
  });

  public allRulesFlat = computed<FEWafRuleModel[]>(() => {
    return this.mainGroups().flatMap((mainGroup) => mainGroup.ruleGroups.flatMap((group) => group.rules));
  });

  public isLoadingRows: { [key: string]: Signal<boolean> } = {};
  private cachedExpandedGroups: StringBooleanMap = {};

  constructor() {
    super();
    const pullZoneId = this.#shieldStore.pullZoneId();

    this.contentService.setTitle('WAF');
    this.contentService.setBackButton({
      routerLink: `/${AppRoutes.CDN}/${pullZoneId}/${AppRoutes.Shield}`,
    });

    this.#shieldStore.clearSubmitted();
    this.#shieldStore.clearUpdated();

    effect(() => {
      const currentSearch = this.debouncedSearch();
      const mainGroups = this.mainGroups();

      untracked(() => {
        this.filteredMainGroups.set(currentSearch.length > 0 ? this.filterWafRules(mainGroups, currentSearch) : mainGroups);
      });
    });

    // Initialize checkedRules for custom and general rules and checkedGroups for general rules
    effect(() => {
      const customRules = this.customRules();
      const updatedCheckedRules: StringBooleanMap = {};
      const mainGroups = this.mainGroups();
      const updatedCheckedGroups: StringBooleanMap = {};

      for (const rule of customRules) {
        if (rule.ruleId) {
          updatedCheckedRules[rule.ruleId] = untracked(() => this.checkedRules()[rule.ruleId || ''] ?? false);
        }
      }

      for (const mainGroup of mainGroups) {
        for (const group of mainGroup.ruleGroups) {
          if (group.id) {
            updatedCheckedGroups[group.id] = untracked(() => this.checkedGroups()[group.id || ''] ?? false);
          }

          for (const rule of group.rules) {
            if (rule.ruleId) {
              updatedCheckedRules[rule.ruleId] = untracked(() => this.checkedRules()[rule.ruleId || ''] ?? false);
            }
          }
        }
      }
      untracked(() => {
        this.checkedRules.set(updatedCheckedRules);
        this.checkedGroups.set(updatedCheckedGroups);
      });
    });

    effect(() => {
      const searchTerm = this.search().trim().toLowerCase();
      const filteredGroups = this.filteredMainGroups();

      if (searchTerm.length > 0) {
        // Cache current state only once, if not already cached
        if (Object.keys(this.cachedExpandedGroups).length === 0) {
          this.cachedExpandedGroups = { ...this.expandedGroups() };
        }

        const newExpandedGroups: StringBooleanMap = {};

        filteredGroups.forEach((mainGroup) => {
          mainGroup.ruleGroups.forEach((group) => {
            newExpandedGroups[group.id || ''] = group.rules.some(
              (rule) => rule.ruleId?.toLowerCase().includes(searchTerm) || rule.description?.toLowerCase().includes(searchTerm),
            );
          });
        });

        untracked(() => this.expandedGroups.set(newExpandedGroups));
      }

      // Search cleared → restore original state if it was cached
      else if (Object.keys(this.cachedExpandedGroups).length > 0) {
        untracked(() => this.expandedGroups.set(this.cachedExpandedGroups));
      }
    });

    // On save settle, clear the override: success keeps the new order, error reverts to the server's.
    effect(() => {
      if (!this.isReorderSaving()) {
        untracked(() => this.orderOverride.set(null));
      }
    });
  }

  public ngOnDestroy(): void {
    this.contentService.setBackButton(null);
  }

  private mapRulesToLocalModel(allRules: WafRuleMainGroupModel[], shield?: ShieldZoneResponse): FEWafRuleMainGroupModel[] {
    const disabledRules = shield?.wafDisabledRules ?? [];
    const logOnlyRules = shield?.wafLogOnlyRules ?? [];

    return allRules.map((mainGroup) => ({
      name: mainGroup.name || '[name]',
      ruleset: mainGroup.ruleset || '[ruleset]',
      ruleGroups:
        mainGroup.ruleGroups
          ?.filter((ruleGroup) => !!ruleGroup.rules?.length) // NOTE: filter out clusters if there is no active rules inside
          ?.map((ruleGroup) => {
            return {
              ...ruleGroup,
              rules:
                ruleGroup.rules?.map((rule) => {
                  this.isLoadingRows[`rule-${rule.ruleId}`] = this.#shieldStore.isLoading(StoreRequest.Update, [
                    `rule-${String(rule.ruleId)}`,
                  ]) as Signal<boolean>;

                  return {
                    ...rule,
                    isEnabled: !disabledRules.includes(String(rule.ruleId)),
                    isLogging: logOnlyRules.includes(String(rule.ruleId)),
                  };
                }) || [],
            };
          }) || [],
    }));
  }

  public deleteWAFRule(ruleId: number) {
    const confirmDialogRef = this.#dialog.show(ConfirmationDialogComponent, {
      class: 'modal-dialog-centered modal-sm',
      initialState: {
        data: {
          contextType: 'danger',
          confirmButtonName: 'Delete',
          description: 'Are you sure you want to delete the custom WAF rule.',
          event: {
            action: 'confirm',
          },
          title: 'Delete custom WAF rule?',
          textCenter: true,
        },
      },
    });

    this.subscriptions.add(
      confirmDialogRef.content?.getOnAction$().subscribe((event) => {
        if (event.action === 'cancel') return this.#dialog.hide();

        this.#shieldStore.deleteCustomWafRule({ ruleId });
      }),
    );
  }

  public dropdownClicked(groupI: number, rowI: number) {
    const key = `${groupI}-${rowI}`;
    const current = this.expandedGroups();
    this.expandedGroups.set({
      ...current,
      [key]: !current[key],
    });
  }

  public toggleActive(ruleId: FEWafRuleModel['ruleId'], isEnabled: boolean) {
    this.actionClick(isEnabled ? 'disable' : 'enable', ruleId);
  }

  public onSelectAllChanged(isChecked: boolean) {
    const updatedRulesMap = { ...this.checkedRules() };
    Object.keys(updatedRulesMap).forEach((key) => (updatedRulesMap[key] = isChecked));
    this.checkedRules.set(updatedRulesMap);

    const updatedGroupsMap = { ...this.checkedGroups() };
    Object.keys(updatedGroupsMap).forEach((key) => (updatedGroupsMap[key] = isChecked));
    this.checkedGroups.set(updatedGroupsMap);
  }

  public onRuleCheckCheckboxToggle(ruleId: FEWafRuleModel['ruleId'], isChecked: boolean) {
    if (!ruleId) return;

    const checkedRules = { ...this.checkedRules() };
    checkedRules[ruleId] = isChecked;
    this.checkedRules.set(checkedRules);
  }

  public onRulesGroupCheckboxToggle(groupId: WafRuleGroupModel['id'], isChecked: boolean) {
    if (!groupId) return;

    // check toggle for group itself
    const checkedGroups = { ...this.checkedGroups() };
    checkedGroups[groupId] = isChecked;
    this.checkedGroups.set(checkedGroups);

    // check toggle for all groups inside
    const checkedRules = { ...this.checkedRules() };
    this.mainGroups().forEach((mainGroup) => {
      mainGroup.ruleGroups
        .filter(({ id }) => id === groupId)
        .forEach((rulesGroup) => {
          rulesGroup.rules.forEach((rule) => {
            if (!rule.ruleId) return;
            checkedRules[rule.ruleId] = isChecked;
          });
        });
    });
    this.checkedRules.set(checkedRules);
  }

  public filterCustomRules(customRules: FEWafRuleModel[], searchTerm: string): FEWafRuleModel[] {
    if (!searchTerm) return customRules;

    return customRules.filter((rule) => rule.description?.toLowerCase().includes(searchTerm.toLowerCase()));
  }

  public filterWafRules(mainGroups: FEWafRuleMainGroupModel[], searchTerm: string): FEWafRuleMainGroupModel[] {
    if (!searchTerm) return mainGroups;

    const lowerSearch = searchTerm.toLowerCase();

    // Step 1: Build a Set of matching rule IDs
    const matchedRuleIds = new Set(
      this.allRulesFlat()
        .filter((rule) => rule.ruleId?.toLowerCase().includes(lowerSearch) || rule.description?.toLowerCase().includes(lowerSearch))
        .map((rule) => rule.ruleId),
    );

    // Step 2: Filter original structure based on matches
    return mainGroups
      .map((mainGroup) => {
        const filteredGroups = mainGroup.ruleGroups
          .map((group) => {
            const filteredRules = group.rules.filter((rule) => matchedRuleIds.has(rule.ruleId));
            return filteredRules.length > 0 ? { ...group, rules: filteredRules } : null;
          })
          .filter((group): group is FEWafRuleGroupModel => group !== null);

        return filteredGroups.length > 0 ? { ...mainGroup, ruleGroups: filteredGroups } : null;
      })
      .filter((mainGroup): mainGroup is FEWafRuleMainGroupModel => mainGroup !== null);
  }

  public actionClick(action: 'expand' | 'collapse' | 'disable' | 'enable' | 'log', ruleId?: FEWafRuleModel['ruleId']) {
    // Return if no rules are checked
    if (!Object.values(this.checkedRules()).some((value) => value) && !ruleId) return;

    const selectedShieldId = this.#shieldStore.selectedEntityId()();
    // Nothing is selected - silently die
    if (!selectedShieldId) return;

    const selectedRuleIds = ruleId
      ? [ruleId]
      : Object.entries(this.checkedRules())
          .filter(([_, isChecked]) => isChecked)
          .map(([id]) => id);

    switch (action) {
      case 'expand': {
        const expandedGroups = { ...this.expandedGroups() };
        const checkedGroups = this.checkedGroups();

        Object.entries(checkedGroups)
          .filter(([_, isChecked]) => isChecked)
          .forEach(([groupId]) => (expandedGroups[groupId] = true));

        this.expandedGroups.set(expandedGroups);
        return;
      }

      case 'collapse': {
        const expandedGroups = { ...this.expandedGroups() };
        const checkedGroups = this.checkedGroups();

        Object.entries(checkedGroups).forEach(([groupId, isChecked]) => {
          if (isChecked) {
            expandedGroups[groupId] = false;
          }
        });

        this.expandedGroups.set(expandedGroups);
        return;
      }

      case 'disable': {
        this.#wafService.disableRules(selectedRuleIds);

        break;
      }

      case 'enable': {
        this.#wafService.enableRules(selectedRuleIds);

        break;
      }

      case 'log': {
        this.#wafService.logRules(selectedRuleIds);

        break;
      }
    }

    // Uncheck all tr checkboxes
    const checkedRules = { ...this.checkedRules() };
    const checkedGroups = { ...this.checkedGroups() };

    this.filteredMainGroups().forEach((mainGroup) => {
      mainGroup.ruleGroups.forEach((rulesGroup) => {
        if (rulesGroup.id) {
          checkedGroups[rulesGroup.id] = false;
        }
        rulesGroup.rules.forEach((rule) => {
          if (rule.ruleId) {
            checkedRules[rule.ruleId] = false;
          }
        });
      });
    });

    this.filteredCustomRules().forEach((rule) => {
      if (rule.ruleId) {
        checkedRules[rule.ruleId] = false;
      }
    });

    this.checkedRules.set(checkedRules);
    this.checkedGroups.set(checkedGroups);

    // Uncheck batch toggle check checkbox
  }

  public onDropdownClick(item: DropdownMenuItem, rule: FEWafRuleModel) {
    if (!item.action) return;
    if (item.action === 'delete' && item.id) return this.deleteWAFRule(+item.id);
    if (item.action === 'edit' && item.id) return this.router.navigate([`edit/${item.id}`], { relativeTo: this.route });

    this.actionClick(item.action as 'expand' | 'disable' | 'enable' | 'log', rule.ruleId);
  }

  public onCustomRuleDrop(event: CdkDragDrop<FEWafRuleModel[]>) {
    const shieldId = this.#shieldStore.selectedEntityId()();
    if (!shieldId) return;

    const currentIds = this.customRules().map((rule) => Number(rule.ruleId));
    const orderedIds = [...currentIds];
    moveItemInArray(orderedIds, event.previousIndex, event.currentIndex);

    if (orderedIds.every((id, i) => id === currentIds[i])) return;

    this.orderOverride.set(orderedIds);
    this.#shieldStore.updateShield({
      id: Number(shieldId),
      data: { wafCustomRuleOrder: orderedIds },
      customFields: ['wafCustomRuleOrder'],
      toastr: { title: 'Custom WAF rule order was updated.' },
    });
  }

  public toggleCollapse(groupId: WafRuleGroupModel['id']) {
    if (!groupId) return;
    const expandedGroups = { ...this.expandedGroups() };
    expandedGroups[groupId] = !expandedGroups[groupId];
    this.expandedGroups.set(expandedGroups);
  }
}
export interface FEWafRuleMainGroupModel extends WafRuleMainGroupModel {
  name: string;
  ruleset: string;
  ruleGroups: FEWafRuleGroupModel[];
}

export interface FEWafRuleGroupModel extends WafRuleGroupModel {
  rules: FEWafRuleModel[];
}

export interface FEWafRuleModel extends WafRuleModel {
  isEnabled: boolean;
  isLogging: boolean;
}
